import React, {createContext, useContext, useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {useLocation, useParams, useSearchParams} from "react-router-dom";
import {API} from "api";
import {useSetting} from "contexts/setting";
import {LoadError} from "pages/LoadError/LoadError";
import {
  CODE_SELECTION,
  liveTemplateSelection,
  parseSceneTemplateMembers,
  type TemplateSelection,
} from "data/templateSlots";

/**
 * Which design of each slot the editor draws (data/templateSlots.ts).
 *
 * The live choice comes once per page load: a banner must not change its
 * design while somebody edits it. A saved scene never reads it, and neither
 * does the renderer (`render=true`): an export draws the scene's own snapshot.
 */

export const TEMPLATE_SLOTS_QUERY_KEY = ["template-slots"] as const;

export type TemplateSlotsDoc = {
  rev: number;
  chosen: string[];
  log: Array<{at: string; by: string; member: string; previous: string[]}>;
  updatedAt: string | null;
  updatedBy: string | null;
};

const LiveTemplateSelectionContext = createContext<TemplateSelection | null>(null);

function isSceneRoute(pathname: string): boolean {
  return pathname.startsWith("/scene/");
}

/**
 * Loads the live choice for the editor. A page without a saved scene waits for
 * it: until then it would draw designs that are not on air. A saved scene and
 * the renderer do not wait.
 */
export function LiveTemplateSelectionGate({children}: {children: React.ReactNode}) {
  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();
  const isRenderMode = searchParams.get("render") === "true";
  const query = useQuery<TemplateSlotsDoc>({
    queryKey: TEMPLATE_SLOTS_QUERY_KEY,
    queryFn: () => API.fetchTemplateSlots(),
    enabled: !isRenderMode,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const live = useMemo(() => {
    if (!query.data) return null;
    const {selection, unknown} = liveTemplateSelection(query.data.chosen);
    if (unknown.length > 0) {
      console.warn(`[template-slots] Unknown designs in the live choice: ${unknown.join(", ")}`);
    }
    return selection;
  }, [query.data]);

  // The renderer draws scenes only; its warm-up page draws the code defaults.
  if (isRenderMode) {
    return (
      <LiveTemplateSelectionContext.Provider value={CODE_SELECTION}>
        {children}
      </LiveTemplateSelectionContext.Provider>
    );
  }
  if (!live && !isSceneRoute(pathname)) {
    if (query.isError) {
      return (
        <LoadError
          title="Templates could not load"
          summary="The list of template designs on air did not load. Try again."
          error={query.error}
          retry={() => query.refetch()}
        />
      );
    }
    return null;
  }
  return (
    <LiveTemplateSelectionContext.Provider value={live}>{children}</LiveTemplateSelectionContext.Provider>
  );
}

/**
 * The designs of the saved scene that this build does not know. App blocks
 * such a scene (libs/loadFailure.ts): it must not draw other designs.
 */
export function useUnknownSceneTemplateMembers(): string[] {
  const {sceneId} = useParams();
  const {templateMembers, dataChannel} = useSetting();
  return useMemo(
    () =>
      sceneId && dataChannel ? parseSceneTemplateMembers(dataChannel, templateMembers).unknown : [],
    [sceneId, dataChannel, templateMembers]
  );
}

/**
 * Which design of each slot this page draws: its saved scene's, or the live
 * choice. A Grid banner (`isControlledBanner`, outside the editor's AppRoot)
 * draws the designs code puts on air: the Grid does not follow the choice yet.
 */
export function useTemplateSelection(): TemplateSelection {
  const {sceneId} = useParams();
  const {templateMembers, dataChannel, isControlledBanner} = useSetting();
  const live = useContext(LiveTemplateSelectionContext);
  const scene = useMemo((): TemplateSelection | null => {
    if (!sceneId) return null;
    // Before the scene loads there is no channel yet, and nothing to draw.
    if (!dataChannel) return {source: "scene", members: []};
    return parseSceneTemplateMembers(dataChannel, templateMembers).selection;
  }, [sceneId, dataChannel, templateMembers]);
  if (isControlledBanner) return CODE_SELECTION;
  if (scene) return scene;
  if (!live) throw new Error("The live template selection is not loaded");
  return live;
}
